import { Button as AntButton } from 'antd'
import { isEmpty } from 'lodash'
import { ReactNode, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import AccessIcon from '@/assets/icons/access.svg?react'
import CodePreviewIcon from '@/assets/icons/code-preview.svg?react'
import EarthIcon from '@/assets/icons/earth.svg?react'
import FingerprintIcon from '@/assets/icons/fingerprint.svg?react'
import KeyIcon from '@/assets/icons/key.svg?react'
import PasswordIcon from '@/assets/icons/password.svg?react'
import { Trans } from '@/components'
import { Dropdown } from '@/components/base/Dropdown'
import { TotpState } from '@/consts/mfa'
import { AppContext } from '@/contexts/AppContext'
import { TeamAppContext } from '@/contexts/TeamContext'
import { supportedLanguages } from '@/i18n'
import { MfaService } from '@/services/mfa-service'
import { useUserStore } from '@/stores/user'

import { DeleteSshPubKeyModal } from './components/DeleteSshPubKeyModal'
import { EditSshPubKeyModal } from './components/EditSshPubKeyModal'
import { MfaDeactivateModal } from './components/MfaDeactivateModal'
import { MfaSetupWizardModal } from './components/MfaSetupWizardModal'
import { NewCliTokenModal } from './components/NewCliTokenModal'
import { NewSdkTokenModal } from './components/NewSdkTokenModal'
import { ProfileHeaderCard } from './components/ProfileHeaderCard'
import { PwChangeModal } from './components/PwChangeModal'
import SshPubKeyComponent from './components/SshPubKeyComponent'

interface SettingItem {
  key: string
  icon: React.ComponentType<{ className?: string }>
  title: string
  desc: string
  showAction?: boolean
  actionTitle?: string
  action?: () => void
  customAction?: ReactNode
  additionalComponent?: ReactNode
}

export function ProfilePage() {
  const userStore = useUserStore()
  const { i18n, t } = useTranslation(['translation', 'accessKeys'])
  const navigate = useNavigate()
  const [deleteSshPubKeyModalOpen, setDeleteSshPubKeyModalOpen] = useState(false)
  const [editSshPubKeyModalOpen, setEditSshPubKeyModalOpen] = useState(false)
  const [cliTokenModalOpen, setCliTokenModalOpen] = useState(false)
  const [sdkTokenModalOpen, setSdkTokenModalOpen] = useState(false)
  const [pwChangeModalOpen, setPwChangeModalOpen] = useState(false)
  const [mfaSetupModalOpen, setMfaSetupModalOpen] = useState(false)
  const [mfaDeactivateModalOpen, setMfaDeactivateModalOpen] = useState(false)
  const [mfaSetupInitialStep, setMfaSetupInitialStep] = useState<1 | 3>(1)
  const [totpState, setTotpState] = useState<number | null>(null)
  const app = useContext(AppContext)
  const teamApp = useContext(TeamAppContext)
  const isTeamContext = teamApp !== null
  const isOauthUser = Boolean(userStore.binds?.GoogleOpenID || userStore.binds?.GithubOpenID)

  const languageOptions = supportedLanguages.map((lang) => ({
    label: lang.name,
    value: lang.code,
  }))

  const handleLanguageChange = (value: string | number | (string | number)[]) => {
    i18n.changeLanguage(value as string)
    app.refresh()
  }

  const refreshTotpStatus = () => {
    MfaService.getStatus()
      .then((status) => setTotpState(status.state))
      .catch((error) => console.error('Failed to get MFA status:', error))
  }

  const handleOpenMfaSetup = (initialStep: 1 | 3 = 1) => {
    setMfaSetupInitialStep(initialStep)
    setMfaSetupModalOpen(true)
  }

  const renderMfaAction = () => {
    if (totpState === null) {
      return null
    }

    if (totpState === TotpState.Enabled) {
      return (
        <AntButton
          className="min-w-[110px] h-[44px] t-sb border border-error text-error hover:!border-error hover:!text-error"
          onClick={() => setMfaDeactivateModalOpen(true)}
        >
          <Trans i18nKey="profile.mfa.disableBtn">Disable</Trans>
        </AntButton>
      )
    }

    if (totpState === TotpState.Pending) {
      return (
        <AntButton
          className="min-w-[110px] h-[44px] t-sb border border-neutral-200"
          onClick={() => handleOpenMfaSetup(3)}
        >
          <Trans i18nKey="profile.mfa.continueBtn">Continue</Trans>
        </AntButton>
      )
    }

    return (
      <AntButton
        className="min-w-[110px] h-[44px] t-sb border border-neutral-200"
        onClick={() => handleOpenMfaSetup(1)}
      >
        <Trans i18nKey="profile.mfa.configureBtn">Configure</Trans>
      </AntButton>
    )
  }

  const platformSettingItems: SettingItem[] = [
    {
      key: 'Language',
      icon: EarthIcon,
      title: t('profile.language.title'),
      desc: t('profile.language.desc'),
      customAction: (
        <Dropdown
          rootClassName="t-sb"
          options={languageOptions}
          value={i18n.language}
          onChange={handleLanguageChange}
          className="w-[160px] h-[44px] t-sm border border-neutral-200"
          menuClassName="w-[200px]"
        />
      ),
    },
  ]

  if (isTeamContext) {
    if (!isOauthUser) {
      platformSettingItems.unshift({
        key: 'Mfa',
        icon: FingerprintIcon,
        title: t('profile.mfa.title'),
        desc: t('profile.mfa.desc'),
        showAction: false,
        customAction: renderMfaAction(),
      })
    }
    platformSettingItems.unshift({
      key: 'AccountPassword',
      icon: PasswordIcon,
      title: t('profile.password.title'),
      desc: t('profile.password.desc'),
      showAction: isEmpty(userStore.binds),
      actionTitle: t('profile.password.updateBtn'),
      action: () => {
        setPwChangeModalOpen(true)
      },
    })
  }

  const developerItems: SettingItem[] = [
    {
      key: 'AccessKeys',
      icon: AccessIcon,
      title: t('title', { ns: 'accessKeys' }),
      desc: t('desc', { ns: 'accessKeys' }),
      showAction: true,
      actionTitle: t('manageBtn', { ns: 'accessKeys' }),
      action: () => navigate(isTeamContext ? '/team/profile/access-keys' : '/profile/access-keys'),
    },
    {
      key: 'SshPublicKey',
      icon: KeyIcon,
      title: t('profile.sshPubKey.title'),
      desc: t('profile.sshPubKey.desc'),
      additionalComponent: userStore.sshPubKey.length > 0 ? <SshPubKeyComponent /> : null,
      customAction:
        userStore.sshPubKey === '' ? (
          <div>
            <AntButton
              className="min-w-[110px] h-[44px] t-sb border border-neutral-200"
              onClick={() => setEditSshPubKeyModalOpen(true)}
            >
              <Trans i18nKey="profile.sshPubKey.addBtn">New SSH Key</Trans>
            </AntButton>
          </div>
        ) : (
          <div className="flex gap-x-4">
            <AntButton
              className="min-w-[110px] h-[44px] t-sb border border-neutral-200"
              onClick={() => setEditSshPubKeyModalOpen(true)}
            >
              <Trans i18nKey="profile.sshPubKey.updateBtn">Update key</Trans>
            </AntButton>
            <AntButton
              className="min-w-[110px] h-[44px] t-sb border border-error text-error hover:!border-error hover:!text-error"
              onClick={() => setDeleteSshPubKeyModalOpen(true)}
            >
              <Trans i18nKey="profile.sshPubKey.deleteBtn">Delete Key</Trans>
            </AntButton>
          </div>
        ),
    },
    {
      key: 'SdkToken',
      icon: CodePreviewIcon,
      title: t('profile.sdkToken.title'),
      desc: t('profile.sdkToken.desc'),
      showAction: true,
      actionTitle: t('profile.sdkToken.newBtn'),
      action: () => setSdkTokenModalOpen(true),
    },
    {
      key: 'CliToken',
      icon: CodePreviewIcon,
      title: t('profile.cliToken.title'),
      desc: t('profile.cliToken.desc'),
      showAction: true,
      actionTitle: t('profile.cliToken.newBtn'),
      action: () => setCliTokenModalOpen(true),
    },
  ]

  useEffect(() => {
    userStore.getSshPubKey()
  }, [])

  useEffect(() => {
    if (isTeamContext && !isOauthUser) {
      refreshTotpStatus()
    }
  }, [isTeamContext, isOauthUser])

  const renderSection = (title: ReactNode, items: SettingItem[]) => (
    <section className="flex flex-col gap-y-8">
      <div className="flex items-center h-[60px] border-b border-neutral-200 h6">
        <h6 className="text-primary">{title}</h6>
      </div>
      <div className="flex flex-col gap-y-2">
        {items.map((item) => (
          <div key={item.key} className="flex items-center py-4 gap-x-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-neutral-50 flex self-start items-center justify-center">
              <item.icon className="w-6 h-6 text-tertiary" />
            </div>
            <div className="flex-grow">
              <div className="flex justify-between items-center">
                <div>
                  <div className="t-bold text-primary">{item.title}</div>
                  <div className="t-xs text-secondary">{item.desc}</div>
                </div>
                {item.showAction && (
                  <AntButton
                    className="min-w-[110px] h-[44px] t-sb border border-neutral-200"
                    onClick={item.action}
                  >
                    {item.actionTitle}
                  </AntButton>
                )}
                {item.customAction && <div>{item.customAction}</div>}
              </div>
              {item.additionalComponent && <div className="mt-3">{item.additionalComponent}</div>}
            </div>
          </div>
        ))}
      </div>
    </section>
  )

  return (
    <div className="flex flex-col h-full">
      <ProfileHeaderCard />

      <div className="card bg-panel-white mt-2.5 h-full overflow-y-auto flex flex-col gap-y-8">
        {renderSection(
          <Trans i18nKey="profile.accountSetting">Platform Setting</Trans>,
          platformSettingItems,
        )}
        {renderSection(
          <Trans i18nKey="profile.developerCredentials">Developer & Credentials</Trans>,
          developerItems,
        )}
      </div>

      {pwChangeModalOpen && (
        <PwChangeModal
          onOk={() => setPwChangeModalOpen(false)}
          onClose={() => setPwChangeModalOpen(false)}
        />
      )}

      {mfaSetupModalOpen && (
        <MfaSetupWizardModal
          initialStep={mfaSetupInitialStep}
          onClose={() => setMfaSetupModalOpen(false)}
          onSuccess={() => {
            setMfaSetupModalOpen(false)
            refreshTotpStatus()
          }}
        />
      )}

      {mfaDeactivateModalOpen && (
        <MfaDeactivateModal
          onClose={() => setMfaDeactivateModalOpen(false)}
          onSuccess={() => {
            setMfaDeactivateModalOpen(false)
            refreshTotpStatus()
          }}
        />
      )}

      {deleteSshPubKeyModalOpen && (
        <DeleteSshPubKeyModal onClose={() => setDeleteSshPubKeyModalOpen(false)} />
      )}

      {editSshPubKeyModalOpen && (
        <EditSshPubKeyModal
          isEdit={userStore.sshPubKey !== ''}
          onClose={() => setEditSshPubKeyModalOpen(false)}
        />
      )}

      {cliTokenModalOpen && <NewCliTokenModal onClose={() => setCliTokenModalOpen(false)} />}
      {sdkTokenModalOpen && <NewSdkTokenModal onClose={() => setSdkTokenModalOpen(false)} />}
    </div>
  )
}
